import { Component, Injector, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import {
    selectSelectedWorkflow,
    selectSelectedWorkflowAsTemplate,
    WorkflowForm,
    WorkflowsActions,
    WorkflowSectionsActions,
    WorkflowSectionsState,
    WorkflowsState,
} from '@modeso/aumico-lib-cpmm-fe';
import { WorkflowConfiguratorComponent } from '@modeso/aumico-lib-cpmm-fe/src/lib/components/workflow-configurator/workflow-configurator.component';
import { CreateWorkflowDto } from '@modeso/aumico-lib-cpmm-fe/src/lib/dtos/create-workflow-dto.interface';
import { UpdateWorkflowDto } from '@modeso/aumico-lib-cpmm-fe/src/lib/dtos/update-workflow-dto.interface';
import { UpdateWorkflowSectionDto } from '@modeso/aumico-lib-cpmm-fe/src/lib/dtos/update-workflow-section-dto.interface';
import { WorkflowDto } from '@modeso/aumico-lib-cpmm-fe/src/lib/dtos/workflow-dto.interface';
import { WorkflowArea } from '@modeso/aumico-lib-cpmm-fe/src/lib/enums/workflow-area.enum';
import { WorkflowFormSection } from '@modeso/aumico-lib-cpmm-fe/src/lib/interfaces/workflow-form-section.interface';
import { WorkflowTaskForm } from '@modeso/aumico-lib-cpmm-fe/src/lib/interfaces/workflow-form-task.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import fastDeepEqual from 'fast-deep-equal';
import { cloneDeep } from 'lodash-es';
import { filter, Observable, of, switchMap, take } from 'rxjs';
import { BasePageComponent } from '../../../../pages/base.page';

@UntilDestroy()
@Component({
    selector: 'app-closing-workflow-configurator',
    templateUrl: './closing-workflow-configurator.page.html',
    styleUrls: ['./closing-workflow-configurator.page.scss'],
})
export class ClosingWorkflowConfiguratorPage extends BasePageComponent {
    savedWorkflow: WorkflowDto | undefined;
    workflowAsTemplate: WorkflowDto | undefined;
    workflowFormValue: WorkflowForm | undefined;
    updateSectionInProgress = false;

    @ViewChild(WorkflowConfiguratorComponent)
    workflowConfigurator!: WorkflowConfiguratorComponent;

    constructor(
        private injector: Injector,
        private workflowsStore: Store<WorkflowsState>,
        private workflowSectionsStore: Store<WorkflowSectionsState>,
        private i18NextService: I18NextService,
        private matDialog: MatDialog,
        private route: ActivatedRoute,
    ) {
        super(injector, matDialog);
    }

    override ngOnInit(): void {
        super.ngOnInit();

        // on page reload: load workflows and select based on URL param
        const workflowId = this.route.snapshot.paramMap.get('id');
        if (workflowId) {
            this.workflowsStore.dispatch(WorkflowsActions.setSelectedWorkflowId({ workflowId }));
            this.workflowsStore.dispatch(
                WorkflowsActions.loadWorkflows({ filter: { area: WorkflowArea.CLOSING_PROCESS } }),
            );
        }

        // Save button is always enabled -> And if the form is invalid, it will show what needs to be fixed
        this.disableProceedButton = false;

        this.actionsListener$
            .pipe(ofType(WorkflowsActions.workflowCreated, WorkflowsActions.workflowUpdated), untilDestroyed(this))
            .subscribe(() => {
                this.onSaveSuccessfully();
            });

        this.actionsListener$
            .pipe(ofType(WorkflowSectionsActions.sectionUpdated), untilDestroyed(this))
            .subscribe(() => {
                this.updateSectionInProgress = false;
                this.fireSuccessNotification({
                    descriptionKey: this.i18NextService.t('aumico_cpmm_section_update_success') as string,
                });
            });

        this.actionsListener$
            .pipe(
                ofType(WorkflowsActions.workflowCreateFailure, WorkflowsActions.workflowUpdateFailure),
                untilDestroyed(this),
            )
            .subscribe((error) => {
                this.fireErrorMessage(this.i18NextService.t('aumico_cpmm_workflow_update_failure') as string);
            });

        this.actionsListener$
            .pipe(ofType(WorkflowSectionsActions.sectionUpdateFailure), untilDestroyed(this))
            .subscribe((error) => {
                this.updateSectionInProgress = false;
                this.fireErrorMessage(this.i18NextService.t('aumico_cpmm_section_update_failure') as string);
            });

        this.workflowsStore
            .select(selectSelectedWorkflow)
            .pipe(
                filter((workflow) => workflow != null),
                untilDestroyed(this),
            )
            .subscribe((workflow) => {
                // NOTE: Workflow will be emitted again upon update section success and it will emit the saved workflow in the reducer overriding any user changes in the workflow configurator form
                // Change the savedWorkflow object only if the workflow form is not initialized yet and this emit is not due a section update
                if (!this.updateSectionInProgress) {
                    this.savedWorkflow = cloneDeep<WorkflowDto>(workflow);
                }
            });

        this.workflowsStore
            .select(selectSelectedWorkflowAsTemplate)
            .pipe(
                filter((workflow) => workflow != null),
                untilDestroyed(this),
            )
            .subscribe((workflow) => {
                this.workflowAsTemplate = cloneDeep<WorkflowDto>({
                    ...workflow,
                    name: (this.i18NextService.t('aumico_cpmm_copy_of') as string) + workflow.name,
                });
            });
    }

    updateSection(data: { sectionId: string; sectionUpdateDto: UpdateWorkflowSectionDto }): void {
        this.updateSectionInProgress = true;
        this.workflowSectionsStore.dispatch(
            WorkflowSectionsActions.updateSection({
                sectionId: data.sectionId,
                updateSectionDto: data.sectionUpdateDto,
            }),
        );
    }

    onWorkflowFormValueChange(workflowFormValue: WorkflowForm): void {
        this.workflowFormValue = cloneDeep<WorkflowForm>(workflowFormValue);
    }

    nextButtonAction = (): void => {
        this.save().subscribe(() => {});
    };

    override save(): Observable<void> {
        // start the confirm+validation flow
        return this.workflowConfigurator.attemptSave().pipe(
            take(1),

            // once we get canSave, either short-circuit or do the actual dispatch
            switchMap((canSave) => {
                if (!canSave) {
                    // user cancelled or form invalid → emit void and complete
                    return of(void 0);
                }

                // user confirmed & form was valid → prune empty sections
                this.workflowFormValue = this.removeEmptySections(this.workflowFormValue);

                if (this.savedWorkflow != null) {
                    // --- UPDATE existing workflow ---
                    const dto: UpdateWorkflowDto = {
                        name: this.workflowFormValue.name.trim(),
                        sectionsToCreate: this.workflowFormValue.sectionsToCreate.map(
                            (section: WorkflowFormSection) => ({
                                name: section.name?.trim() || undefined,
                                id: section.id || undefined,
                                workflowTasks: section.workflowTasks.map((task: WorkflowTaskForm) => ({
                                    workflowTaskId: task.workflowTaskId || undefined,
                                    description: task.description.trim(),
                                    taskType: task.taskType,
                                    scheduleDay: task.scheduleDay?.id || undefined,
                                    scheduleMonth: task.scheduleMonth?.id || undefined,
                                    schedulePeriod: task.schedulePeriod?.id || undefined,
                                })),
                            }),
                        ),
                    };

                    this.workflowsStore.dispatch(
                        WorkflowsActions.updateWorkflow({
                            workflowId: this.savedWorkflow.workflowId,
                            updateWorkflowDto: dto,
                        }),
                    );
                } else {
                    // --- CREATE new workflow ---
                    const dto: CreateWorkflowDto = {
                        name: this.workflowFormValue.name.trim(),
                        area: WorkflowArea.CLOSING_PROCESS,
                        sectionsToCreate: this.workflowFormValue.sectionsToCreate.map(
                            (section: WorkflowFormSection) => ({
                                name: section.name.trim(),
                                id: section.id || undefined,
                                workflowTasks: section.workflowTasks.map((task: WorkflowTaskForm) => ({
                                    description: task.description.trim(),
                                    taskType: task.taskType,
                                    scheduleDay: task.scheduleDay?.id || undefined,
                                    scheduleMonth: task.scheduleMonth?.id || undefined,
                                    schedulePeriod: task.schedulePeriod?.id || undefined,
                                })),
                            }),
                        ),
                    };

                    this.workflowsStore.dispatch(WorkflowsActions.createWorkflow({ createWorkflowDto: dto }));
                }

                // finally, emit a void to signal completion
                return of(void 0);
            }),
            untilDestroyed(this),
        );
    }

    private removeEmptySections(workflowForm: WorkflowForm): WorkflowForm {
        return {
            ...workflowForm,
            sectionsToCreate: workflowForm.sectionsToCreate.filter(
                (section) => section.workflowTasks && section.workflowTasks.length > 0,
            ),
        };
    }

    protected override isDataChanged(): Observable<boolean> {
        // This is what is currently in the form
        const dto: UpdateWorkflowDto = {
            name: this.workflowFormValue?.name?.trim(),
            sectionsToCreate: (this.workflowFormValue?.sectionsToCreate ?? []).map((section: WorkflowFormSection) => ({
                name: section?.name?.trim() ?? undefined,
                id: section?.id ?? undefined,
                workflowTasks: (section?.workflowTasks ?? []).map((task: WorkflowTaskForm) => ({
                    description: task?.description?.trim() ?? undefined,
                    taskType: task?.taskType ?? undefined,
                    scheduleDay: task?.scheduleDay?.id ?? undefined,
                    scheduleMonth: task?.scheduleMonth?.id ?? undefined,
                    schedulePeriod: task?.schedulePeriod?.id ?? undefined,
                })),
            })),
        };

        // This changes what is saved in the store to compare with the form
        if (this.savedWorkflow) {
            const savedDto: UpdateWorkflowDto = {
                name: this.savedWorkflow.name.trim(),
                sectionsToCreate: this.savedWorkflow.sections.map((section) => {
                    const sectionTasks = this.savedWorkflow.workflowTasks.filter(
                        (task) => task.sectionId === section.id,
                    );

                    return {
                        id: section.id,
                        name: section.name.trim(),
                        workflowTasks: sectionTasks.map((task) => ({
                            description: task.description.trim(),
                            taskType: task.taskType,
                            scheduleDay: task.scheduleDay ?? undefined,
                            scheduleMonth: task.scheduleMonth ?? undefined,
                            schedulePeriod: task.schedulePeriod ?? undefined,
                        })),
                    };
                }),
            };
            return of(!fastDeepEqual(dto, savedDto));
        }
        return of(true);
    }

    onSaveSuccessfully(): void {
        this.fireSuccessNotification({
            descriptionKey: this.i18NextService.t('aumico_cpmm_workflow_save_success') as string,
        });
        this.savedSuccessfully.next(true);
    }
}
